import { CancelTokenSource } from 'axios';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getCancelTokenSource } from '../../api/client';
import {
  addNote,
  searchNotes,
  updateNote,
  removeNote,
} from '../../api/Customer';
import { CustomerNote, FetchNotesResponse } from '../../types/noteTypes';
import { CustomerLocationState } from '../../types/types';

// State interface for NotesContext
export interface NotesState {
  notes: CustomerNote[];
  loading: boolean;
  hasApiError: boolean;
  hasMore: boolean;
  totalCount: number;
  searchLoading: boolean;
  addingNote: boolean;
  currentPage: number;
  pageSize: number;
  isSearchMode: boolean;
  searchQuery: string;
}

// Dispatch interface for NotesContext actions
export interface NotesDispatchState {
  loadNotes: (
    customerId: string,
    cancelToken?: CancelTokenSource
  ) => Promise<void>;
  loadMoreNotes: (customerId: string) => Promise<void>;
  addNewNote: (
    customerId: string,
    noteText: string,
    noteType?: string
  ) => Promise<void>;
  updateNote: (
    customerId: string,
    note: string,
    customerNotesId: string
  ) => Promise<void>;
  removeNote: (customerId: string, customerNotesId: string) => Promise<void>;
  searchCustomerNotes: (customerId: string, query: string) => Promise<void>;
  clearSearch: (customerId: string) => void;
  refreshNotes: (customerId: string) => Promise<void>;
}

// Create contexts
export const NotesStateContext = createContext<NotesState>({} as NotesState);

export const NotesDispatchContext = createContext<NotesDispatchState>(
  {} as NotesDispatchState
);

// Initial state for notes context
const initialState: NotesState = {
  notes: [],
  loading: false,
  hasApiError: false,
  hasMore: true,
  totalCount: 0,
  searchLoading: false,
  addingNote: false,
  currentPage: 0,
  pageSize: 20,
  isSearchMode: false,
  searchQuery: '',
};

// Provider component
export const NotesProvider = (props: { children: ReactNode }) => {
  const [state, setState] = useState<NotesState>(initialState);
  const location = useLocation<CustomerLocationState>();

  // Update state helper function
  const updateState = useCallback((updates: Partial<NotesState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Load initial notes
  const loadNotes = useCallback(
    async (customerId: string, cancelToken?: CancelTokenSource) => {
      if (!customerId) return;

      const cancelTokenToUse = cancelToken || getCancelTokenSource();
      updateState({ loading: true, hasApiError: false });

      try {
        const response: FetchNotesResponse = await searchNotes(
          customerId,
          '',
          state.pageSize,
          0,
          cancelTokenToUse.token
        );

        setState((prev) => ({
          ...prev,
          notes: response?.customerNotes || [],
          hasMore: (response?.customerNotes?.length || 0) > 0,
          totalCount: response?.customerNotes?.length || 0,
          loading: false,
          hasApiError: false,
          addingNote: false,
          currentPage: 1,
          isSearchMode: false,
          searchQuery: '',
        }));
      } catch (error: any) {
        if (!cancelTokenToUse.token.reason) {
          updateState({
            loading: false,
            addingNote: false,
            hasApiError: true,
          });
        }
      }
    },
    [state.pageSize, updateState]
  );

  // Load more notes (pagination)
  const loadMoreNotes = useCallback(async (customerId: string) => {
    setState((prev) => {
      if (!customerId || prev.loading || !prev.hasMore || prev.isSearchMode) {
        return prev;
      }

      const offset = prev.notes.length;
      const cancelTokenSource = getCancelTokenSource();

      searchNotes(
        customerId,
        prev.isSearchMode ? prev.searchQuery : '',
        prev.pageSize,
        offset,
        cancelTokenSource.token
      )
        .then((response: FetchNotesResponse) => {
          setState((current) => ({
            ...current,
            notes: [...current.notes, ...(response?.customerNotes || [])],
            hasMore: (response?.customerNotes?.length || 0) > 0,
            totalCount:
              current.totalCount + (response?.customerNotes?.length || 0),
            loading: false,
            hasApiError: false,
            currentPage: current.currentPage + 1,
          }));
        })
        .catch((error: any) => {
          // eslint-disable-next-line no-console
          console.error('Error loading more notes:', error);
          if (!cancelTokenSource.token.reason) {
            setState((current) => ({
              ...current,
              loading: false,
              hasApiError: true,
            }));
          }
        });

      return { ...prev, loading: true, hasApiError: false };
    });
  }, []);

  // Add new note
  const addNewNote = useCallback(
    async (customerId: string, noteText: string, noteType = 'STMT') => {
      if (!customerId || !noteText.trim()) return;

      updateState({ addingNote: true, hasApiError: false });

      try {
        const response = await addNote(customerId, noteText.trim(), noteType);
        if (response?.customerNotesId) {
          await loadNotes(customerId);
        } else {
          updateState({ addingNote: false, hasApiError: true });
        }
      } catch (error: any) {
        updateState({ addingNote: false, hasApiError: true });
      }
    },
    [loadNotes, updateState]
  );

  // Update existing note
  const updateExistingNote = useCallback(
    async (customerId: string, note: string, customerNotesId: string) => {
      if (!customerId || !note.trim() || !customerNotesId) return;

      updateState({ addingNote: true, hasApiError: false });

      try {
        await updateNote(customerId, note.trim(), customerNotesId);
        await loadNotes(customerId);
      } catch (error: any) {
        updateState({ addingNote: false, hasApiError: true });
      }
    },
    [loadNotes, updateState]
  );

  // Remove note
  const removeExistingNote = useCallback(
    async (customerId: string, customerNotesId: string) => {
      if (!customerId || !customerNotesId) return;

      updateState({ addingNote: true, hasApiError: false });

      try {
        await removeNote(customerId, customerNotesId);
        await loadNotes(customerId);
      } catch {
        updateState({ addingNote: false, hasApiError: true });
      }
    },
    [loadNotes, updateState]
  );

  // Clear search
  const clearSearch = useCallback(
    (customerId: string) => {
      setState((prev) => ({
        ...prev,
        isSearchMode: false,
        searchQuery: '',
      }));

      loadNotes(customerId);
    },
    [loadNotes]
  );

  // Search notes
  const searchCustomerNotes = useCallback(
    async (customerId: string, query: string) => {
      if (!customerId) return;

      updateState({ searchLoading: true, hasApiError: false });

      if (!query.trim()) {
        updateState({ searchLoading: false });
        clearSearch(customerId);
        return;
      }

      const cancelTokenSource = getCancelTokenSource();

      try {
        const response = await searchNotes(
          customerId,
          query.trim(),
          state.pageSize,
          0,
          cancelTokenSource.token
        );

        setState((prev) => ({
          ...prev,
          notes: response?.customerNotes || [],
          hasMore: (response?.customerNotes?.length || 0) > 0,
          totalCount: response?.customerNotes?.length || 0,
          searchLoading: false,
          hasApiError: false,
          isSearchMode: true,
          searchQuery: query,
          currentPage: 1,
        }));
      } catch (error: any) {
        if (!cancelTokenSource.token.reason) {
          updateState({
            searchLoading: false,
            hasApiError: true,
          });
        }
      }
    },
    [state.pageSize, updateState, clearSearch]
  );

  // Refresh notes based on current state (search or normal)
  const refreshNotes = useCallback(
    (customerId: string) => {
      return searchCustomerNotes(customerId, state.searchQuery || '');
    },
    [state.searchQuery, searchCustomerNotes]
  );

  // Load notes for current customer when location changes
  useEffect(() => {
    const customerId =
      location?.state?.customer?.customerId ||
      location?.pathname?.split('/')[3];

    if (customerId) {
      const cancelToken = getCancelTokenSource();
      loadNotes(customerId, cancelToken);

      return () => {
        cancelToken.cancel('Component unmounted');
      };
    }
  }, [location, loadNotes]);

  return (
    <NotesStateContext.Provider
      value={{
        ...state,
      }}
    >
      <NotesDispatchContext.Provider
        value={{
          loadNotes,
          loadMoreNotes,
          addNewNote,
          updateNote: updateExistingNote,
          removeNote: removeExistingNote,
          searchCustomerNotes,
          clearSearch,
          refreshNotes,
        }}
      >
        {props.children}
      </NotesDispatchContext.Provider>
    </NotesStateContext.Provider>
  );
};

// Hook to access notes state
export const useNotes = () => useContext(NotesStateContext);

// Hook to access notes actions
export const useNotesActions = () => useContext(NotesDispatchContext);
